import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { MirrorService } from 'services/MirrorService';

import { CreditsFunnels } from 'helpers/funnels/funnels';
import { isMonetizationPage } from 'helpers/route';
import { useCreditsAnalytics } from 'hooks/analytics/useCreditsAnalytics';
import { useCreditWelcomeGiftMutation } from 'hooks/mutations/useCreditWelcomeGiftMutation';
import { updateFeatureFlag } from 'store/common/commonSlice';
import { setCreditsAmount } from 'store/payment/paymentSlice';
import { getCreditsCurrencySelector } from 'store/payment/selectors';

import { ReactComponent as GiftIcon } from 'assets/icons/gift.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { ImagedPopup } from 'components/UI/ImagedPopup';

import thumbnailImg from './images/thumbnail.jpg';
import thumbnailMePlusYouImg from './images/thumbnail_meplusyou.jpg';
import thumbnailMidImg from './images/thumbnail_mid.jpg';

import css from './creditsWelcomeGiftPopup.module.sass';

const getCurrentThumbnailImg = () => {
  if (MirrorService.isMidsOrDatesMe || MirrorService.isSimpleDates) {
    return thumbnailMidImg;
  }

  if (MirrorService.isMePlusYouOrLocalsjoy) {
    return thumbnailMePlusYouImg;
  }

  return thumbnailImg;
};

interface Props {
  onClose: () => void;
  params?: {
    isNewUser: boolean;
  };
}

export const CreditsWelcomeGiftPopup: React.FC<Props> = ({
  params,
  onClose,
}) => {
  const { t } = useTranslation('app');
  const location = useLocation();
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);

  const creditsCurrency = useSelector(getCreditsCurrencySelector);

  const buttonType =
    MirrorService.isMePlusYouOrLocalsjoy ||
    MirrorService.isDreamsDate ||
    MirrorService.isTiamo
      ? ButtonTypes.Primary
      : ButtonTypes.Accent;

  const {
    trackVerticaCreditWelcomeGift,
    trackVerticaCreditWelcomeGiftRetention,
  } = useCreditsAnalytics();
  const welcomeGiftMutation = useCreditWelcomeGiftMutation({
    onSuccess: (data) => {
      setCreditsAmount(data.credits);
      setIsVisible(true);

      params?.isNewUser
        ? trackVerticaCreditWelcomeGift({ operation: 'show' })
        : trackVerticaCreditWelcomeGiftRetention({ operation: 'show' });
    },
  });

  const handleCloseClick = () => {
    params?.isNewUser
      ? trackVerticaCreditWelcomeGift({ operation: 'close' })
      : trackVerticaCreditWelcomeGiftRetention({ operation: 'close' });

    onClose();
  };

  const handleButtonClick = () => {
    params?.isNewUser
      ? trackVerticaCreditWelcomeGift({ operation: 'click' })
      : trackVerticaCreditWelcomeGiftRetention({ operation: 'click' });

    onClose();
  };

  useEffect(() => {
    if (!isMonetizationPage(location.pathname) && welcomeGiftMutation.isIdle) {
      welcomeGiftMutation.mutate({ funnel: CreditsFunnels.WelcomeGift });
    }

    return () => {
      dispatch(
        updateFeatureFlag({
          flag: 'isCanShowGiftCreditsPopup',
          value: false,
        })
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (isVisible) {
    return (
      <ImagedPopup
        onClose={handleCloseClick}
        image={getCurrentThumbnailImg()}
        icon={<GiftIcon />}
      >
        <p className={css.title}>
          {params?.isNewUser
            ? t('creditsWelcomeGiftPopup.titleForNewUser', {
                count: welcomeGiftMutation.data?.credits,
                currency: creditsCurrency,
              })
            : t('creditsWelcomeGiftPopup.title', {
                count: welcomeGiftMutation.data?.credits,
                currency: creditsCurrency,
              })}
        </p>
        {params?.isNewUser || MirrorService.isSimpleDates ? (
          <p className={css.description}>
            {t('creditsWelcomeGiftPopup.description')}
          </p>
        ) : (
          <ul className={css.list}>
            <li className={css.listItem}>
              {t('creditsWelcomeGiftPopup.benefits.chats')}
            </li>
            <li className={css.listItem}>
              {t('creditsWelcomeGiftPopup.benefits.boost')}
            </li>
            <li className={css.listItem}>
              {t('creditsWelcomeGiftPopup.benefits.videos')}
            </li>
          </ul>
        )}
        <BaseButton
          type={buttonType}
          className={css.button}
          onClick={handleButtonClick}
        >
          {t('creditsWelcomeGiftPopup.benefits.submitBtn')}
        </BaseButton>
        {!!MirrorService.supportPhone && (
          <p className={css.supportPhone}>{MirrorService.supportPhone}</p>
        )}
      </ImagedPopup>
    );
  }

  return null;
};
